import {
    createEntityAdapter,
    createSelector,
    createSlice,
    PayloadAction,
} from '@reduxjs/toolkit'
import { SchoolBookingType } from '../firebase/types-bookings'
import { RootState } from './store'
import dayjs, { Dayjs } from 'dayjs'

export type SchoolReplacementRequest = {
    cancelledTeacherLogo?: string
    cancelledTeacherName?: string
    cancelledTeacherUid?: string
    startTime: number
    type: SchoolBookingType
    interest: 'school' | 'supportStaff'
}
export type SchoolReplacementEntity = {
    id: string
} & SchoolReplacementRequest
export const makeSchoolReplacementEntity = (
    id: string,
    data: SchoolReplacementRequest
) => ({
    id,
    ...data,
})

const schoolReplacementsAdapter = createEntityAdapter<SchoolReplacementEntity>({
    selectId: (el) => el.id,
    sortComparer: (a, b) => b.startTime - a.startTime,
})

const initialState = schoolReplacementsAdapter.getInitialState()

export const schoolReplacementsSlice = createSlice({
    name: 'schoolReplacements',
    initialState,
    reducers: {
        upsertSchoolReplacements: (
            state,
            action: PayloadAction<SchoolReplacementEntity[]>
        ) => {
            schoolReplacementsAdapter.upsertMany(state, action.payload)
        },
        upsertSchoolReplacement: (
            state,
            action: PayloadAction<SchoolReplacementEntity>
        ) => {
            schoolReplacementsAdapter.upsertOne(state, action.payload)
        },
        removeSchoolReplacement: (state, action: PayloadAction<string>) => {
            schoolReplacementsAdapter.removeOne(state, action.payload)
        },

        resetSchoolReplacements: () => {
            return initialState
        },
    },
})

export const {
    selectAll: selectAllSchoolReplacements,
    selectById: selectSchoolReplacementById,
    selectIds: selectSchoolReplacementsIds,
} = schoolReplacementsAdapter.getSelectors(
    (state: RootState) => state.schoolReplacements
)

/**
 * Creates a selector fn that returns school keys by given date
 * @param state
 * @param date Formatted as YYYY-MM-DD
 * @returns
 */
export const makeSelectReplacementsForDate = () =>
    createSelector(
        [selectAllSchoolReplacements, (_: RootState, date: Dayjs) => date],
        (bookings, date) =>
            bookings.filter((booking) =>
                dayjs
                    .unix(booking.startTime)
                    .tz('Australia/Melbourne')
                    .isSame(date, 'd')
            )
    )

export const {
    upsertSchoolReplacement,
    upsertSchoolReplacements,
    removeSchoolReplacement,
    resetSchoolReplacements,
} = schoolReplacementsSlice.actions
export default schoolReplacementsSlice.reducer
